// Types
import type {
  TAutomatedEmailUserTemplateApi,
  TAutomatedEmailUserTriggerApi,
  TEmailTemplate,
  TTrigger,
} from '~/modules/opportunities/automated-email/types';
import type {
  TKnownUserTriggerName,
  TTriggerDisplayMeta,
} from '~/modules/opportunities/automated-email/settings/types';

/** Display order in the Settings UI */
const TRIGGER_NAME_UI_ORDER: readonly TKnownUserTriggerName[] = [
  'market-success',
  'reduced',
  'withdrawn',
];

const TRIGGER_NAME_DISPLAY: Record<TKnownUserTriggerName, TTriggerDisplayMeta> = {
  'market-success': {
    name: 'Market success',
    description:
      'When a property was sold, the system finds highly relevant nearby/similar homes and sends a positive message',
  },
  reduced: {
    name: 'Reduced',
    description:
      'When a property price is reduced, the system identifies contacts who may be interested and sends a timely alert',
  },
  withdrawn: {
    name: 'Withdrawn',
    description:
      'When a property is taken off the market, the system identifies contacts who may still be considering selling or letting',
  },
};

const KNOWN_USER_TRIGGER_NAMES: ReadonlySet<string> = new Set<string>(TRIGGER_NAME_UI_ORDER);

export function isKnownUserTriggerName(name: string): name is TKnownUserTriggerName {
  return KNOWN_USER_TRIGGER_NAMES.has(name);
}

function triggerUiOrderIndex(triggerName: string): number {
  if (!isKnownUserTriggerName(triggerName)) {
    return Number.MAX_SAFE_INTEGER;
  }

  return TRIGGER_NAME_UI_ORDER.indexOf(triggerName);
}

function compareTriggersByUiOrder(
  a: TAutomatedEmailUserTriggerApi,
  b: TAutomatedEmailUserTriggerApi
): number {
  const sortA: number = triggerUiOrderIndex(a.trigger_name);
  const sortB: number = triggerUiOrderIndex(b.trigger_name);

  if (sortA !== sortB) {
    return sortA - sortB;
  }

  return a.id - b.id;
}

function sortUserTriggersByUiOrder(
  rows: TAutomatedEmailUserTriggerApi[]
): TAutomatedEmailUserTriggerApi[] {
  return [...rows].sort(compareTriggersByUiOrder);
}

function displayForTriggerName(triggerName: string): TTriggerDisplayMeta {
  if (isKnownUserTriggerName(triggerName)) {
    return TRIGGER_NAME_DISPLAY[triggerName];
  }

  return {
    name: triggerName,
    description: '',
  };
}

function mapApiTemplateRowToEmailTemplate(
  template: TAutomatedEmailUserTemplateApi
): TEmailTemplate {
  return {
    id: template.id,
    title: template.subject,
    content: template.content,
    is_linked: template.is_active,
  };
}

function templatesForTrigger(
  apiTemplates: TAutomatedEmailUserTemplateApi[],
  triggerId: number
): TAutomatedEmailUserTemplateApi[] {
  return apiTemplates
    .filter((t: TAutomatedEmailUserTemplateApi): boolean => t.patch_trigger_id === triggerId)
    .sort(
      (a: TAutomatedEmailUserTemplateApi, b: TAutomatedEmailUserTemplateApi): number =>
        a.id - b.id
    );
}

export function mapUserTriggersAndTemplatesToTriggers(
  apiTriggers: TAutomatedEmailUserTriggerApi[],
  apiTemplates: TAutomatedEmailUserTemplateApi[]
): TTrigger[] {
  const ordered: TAutomatedEmailUserTriggerApi[] = sortUserTriggersByUiOrder(apiTriggers);

  return ordered.map((row: TAutomatedEmailUserTriggerApi): TTrigger => {
    const { name, description }: TTriggerDisplayMeta = displayForTriggerName(row.trigger_name);
    const forTrigger: TAutomatedEmailUserTemplateApi[] = templatesForTrigger(
      apiTemplates,
      row.id
    );

    return {
      id: row.id,
      is_active: row.is_active,
      name,
      description,
      templates: forTrigger.map(mapApiTemplateRowToEmailTemplate),
    };
  });
}

export type { TKnownUserTriggerName, TTriggerDisplayMeta };
